import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tags } from 'lucide-react';
import { useApiClient } from '../../lib/api/api-context.js';
import { getReadUrl } from '../../lib/media/media-api.js';

/**
 * The picture on a product.
 *
 * A media id rather than a URL, for the same reason the stock heading's thumbnail is: read URLs
 * are signed and short-lived, while a price list is a screen somebody leaves open. So the URL is
 * fetched per row actually on screen, and the tag icon stands in — permanently, for the many
 * lines a shop never photographs, which must not look like a broken image.
 */
export function ProductImage({
  mediaId,
  alt,
}: {
  mediaId: string | null;
  alt: string;
}): ReactElement {
  const client = useApiClient();

  const readUrl = useQuery({
    queryKey: ['media', mediaId, 'url', 'thumb'],
    queryFn: () => getReadUrl(client, mediaId as string, 'thumb'),
    enabled: mediaId !== null,
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });

  if (mediaId && readUrl.data) {
    return (
      <img
        src={readUrl.data.url}
        alt={alt}
        loading="lazy"
        className="size-12 shrink-0 rounded-md object-cover"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="flex size-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
    >
      <Tags className="size-5" />
    </span>
  );
}
